import React, { useEffect, useState } from "react";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile } from "../lib/api";
import { AdminListFilters, applyAdminFilters, EMPTY_FILTERS } from "./AdminListFilters";

const eur = (n) => `${Number(n || 0).toFixed(2)}€`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "");

export function InvoiceRegister() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  useEffect(() => { api.get("/admin/invoices").then((r) => setData(r.data)).catch(console.error); }, []);

  const exportCsv = () => downloadFile("/admin/invoices/export.csv", `registre-factures-${new Date().toISOString().slice(0, 10)}.csv`).catch(() => toast.error("Erreur export"));
  const openPdf = (r) => downloadFile(`/admin/orders/${r.order_id}/invoice`, `facture-${r.invoice_number}.pdf`).catch(() => toast.error("PDF indisponible"));

  if (!data) return <p className="text-zinc-500">Chargement...</p>;
  const monthKey = (iso) => { const d = new Date(iso); return `${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`; };
  const byMonth = month ? data.invoices.filter((r) => monthKey(r.invoiced_at) === month) : data.invoices;
  const rows = applyAdminFilters(byMonth, filters, "invoiced_at");
  const rowsTtc = rows.reduce((s, r) => s + (r.total_ttc || 0), 0);

  return (
    <div data-testid="admin-invoices">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-anton text-2xl uppercase text-white">Registre des factures <span className="text-neon-magenta">({data.total})</span></h2>
          <p className="text-zinc-500 text-sm">Numérotation continue STUFF-AAAA-00001, attribuée à la livraison · Total facturé : <span className="text-neon-green font-bold" data-testid="invoices-sum">{eur(data.sum_ttc)}</span> TTC</p>
        </div>
        <button data-testid="invoices-export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-2 hover:border-neon-green hover:text-neon-green"><Download className="h-4 w-4" /> Export CSV (comptable)</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-6">
        {data.months.map((m) => (
          <button key={m.month} data-testid="invoices-month" onClick={() => setMonth(month === m.month ? "" : m.month)}
            className={`text-left border p-3 transition-colors ${month === m.month ? "border-neon-magenta bg-neon-magenta/10" : "border-zinc-800 bg-ink-surface hover:border-zinc-600"}`}>
            <p className="text-zinc-400 text-xs uppercase font-bold">{m.month}</p>
            <p className="text-white font-anton text-lg">{eur(m.total_ttc)}</p>
            <p className="text-zinc-500 text-[11px]">{m.count} facture{m.count > 1 ? "s" : ""} · HT {eur(m.total_ht)} · TVA {eur(m.tva)}</p>
          </button>
        ))}
      </div>

      <AdminListFilters rows={data.invoices} filters={filters} setFilters={setFilters} shown={rows.length} total={byMonth.length} testPrefix="invoices-filter" />
      {(filters.from || filters.to || filters.vendor) && rows.length > 0 && <p data-testid="invoices-filter-sum" className="text-zinc-400 text-xs mb-3">Sélection : <span className="text-neon-green font-bold">{eur(rowsTtc)}</span> TTC sur {rows.length} facture{rows.length > 1 ? "s" : ""}</p>}
      {rows.length === 0 && <p className="text-zinc-500 py-10 text-center">Aucune facture{month ? ` pour ${month}` : ""}{filters.from || filters.to || filters.vendor ? " pour ces filtres" : ""}.</p>}
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-zinc-500 uppercase text-xs border-b border-zinc-800">
              <th className="text-left py-2 px-2">N° facture</th><th className="text-left px-2">Date</th><th className="text-left px-2">Client</th><th className="text-left px-2">Vendeur</th><th className="text-left px-2">Paiement</th><th className="text-right px-2">HT</th><th className="text-right px-2">TVA</th><th className="text-right px-2">TTC</th><th className="text-right px-2">PDF</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.invoice_number} data-testid="invoice-row" className="border-b border-zinc-900 text-zinc-300">
                  <td className="py-2 px-2 font-mono text-neon-magenta">{r.invoice_number}<br /><span className="text-zinc-600 text-[10px]">#{r.ref}</span></td>
                  <td className="px-2 whitespace-nowrap">{fmt(r.invoiced_at)}</td>
                  <td className="px-2">{r.customer_name}<br /><span className="text-zinc-600 text-xs">{r.customer_phone}</span></td>
                  <td className="px-2">{r.vendor_name}<br /><span className="text-zinc-600 text-xs">{r.city}</span></td>
                  <td className="px-2 text-xs">{(r.payment_method || "").toLowerCase().includes("carte") ? "💳 CB" : "💶 Espèces"}</td>
                  <td className="px-2 text-right">{eur(r.total_ht)}</td>
                  <td className="px-2 text-right">{eur(r.tva)}</td>
                  <td className="px-2 text-right text-neon-green font-bold">{eur(r.total_ttc)}</td>
                  <td className="px-2 text-right"><button data-testid="invoice-pdf-btn" onClick={() => openPdf(r)} className="inline-flex items-center gap-1 text-xs text-zinc-300 border border-zinc-700 px-2 py-1 hover:border-neon-magenta hover:text-neon-magenta"><FileText className="h-3 w-3" /> PDF</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
